import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import type { RealtimeState, RealtimeTransport, SubscribedContext, SubscriptionHandlers } from '@/lib/realtime';
import { wsUrl } from '@/lib/realtime';
import { createCentrifugeTransport } from './centrifugeTransport';

/**
 * One realtime connection per signed-in user (design 4.1). The token is the session's GoTrue JWT, read
 * fresh on every (re)connect; the subscribe proxy authorizes each channel, so there is no subscription
 * token. Channels are reference counted: two components on one channel share one subscription.
 *
 * Nothing here holds data: publications are hints that each screen turns into a re-read of its own query
 * (`useLiveRefresh`). With `realtime: false` in config.js, or no session, the state says so and the
 * screens poll.
 */

export interface Realtime {
  state: RealtimeState;
  /** Subscribe to `channel` while the returned cleanup has not run. */
  subscribe(channel: string, handlers: SubscriptionHandlers): () => void;
}

const RealtimeContext = React.createContext<Realtime | null>(null);

interface Entry {
  handlers: Set<SubscriptionHandlers>;
  unsubscribe: () => void;
}

function realtimeEnabled(): boolean {
  return window.__COREWAY_CONFIG__?.realtime !== false;
}

function defaultTransport(): RealtimeTransport {
  const base = window.__COREWAY_CONFIG__?.apiBaseUrl?.replace(/\/$/, '') || window.location.origin;
  return createCentrifugeTransport(wsUrl(base));
}

export function RealtimeProvider({ transport, children }: { transport?: RealtimeTransport; children: React.ReactNode }) {
  const { client, user } = useAuth();
  const userId = user?.id ?? null;
  const enabled = realtimeEnabled();
  const [ownTransport] = React.useState<RealtimeTransport | null>(() => (transport || !enabled ? null : defaultTransport()));
  const t = transport ?? ownTransport;
  const [state, setState] = React.useState<RealtimeState>(enabled ? 'disconnected' : 'disabled');
  const entries = React.useRef(new Map<string, Entry>());

  React.useEffect(() => {
    if (!t || !enabled) return;
    return t.onState(setState);
  }, [t, enabled]);

  // Connect for the signed-in user; a new user (or a sign-out) tears the old connection down first.
  React.useEffect(() => {
    if (!t || !enabled || !userId) return;
    t.connect(async () => {
      const { data } = await client.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('no session');
      return token;
    });
    return () => t.disconnect();
  }, [t, enabled, userId, client]);

  const subscribe = React.useCallback(
    (channel: string, handlers: SubscriptionHandlers) => {
      if (!t || !enabled) return () => {};
      const map = entries.current;
      let entry = map.get(channel);
      if (!entry) {
        const set = new Set<SubscriptionHandlers>();
        const fan: SubscriptionHandlers = {
          onPublication: (data) => set.forEach((h) => h.onPublication(data)),
          onSubscribed: (ctx: SubscribedContext) => set.forEach((h) => h.onSubscribed(ctx)),
          onError: () => set.forEach((h) => h.onError()),
        };
        entry = { handlers: set, unsubscribe: () => {} };
        map.set(channel, entry);
        entry.unsubscribe = t.subscribe(channel, fan);
      }
      entry.handlers.add(handlers);
      const own = entry;
      return () => {
        own.handlers.delete(handlers);
        if (own.handlers.size === 0 && map.get(channel) === own) {
          map.delete(channel);
          own.unsubscribe();
        }
      };
    },
    [t, enabled],
  );

  const value = React.useMemo<Realtime>(() => ({ state: enabled ? state : 'disabled', subscribe }), [state, enabled, subscribe]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): Realtime {
  const ctx = React.useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used inside RealtimeProvider');
  return ctx;
}
